import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { PostListItem } from '@tbbt/shared';
import { Card, Chip } from '@/components/ui';
import { CARD_IMAGE_SIZES, mediaSrcSet, mediaUrl } from '@/lib/media';
import { formatDate } from '@/lib/format';

/**
 * A blog post summary card.
 *
 * The whole card is one link rather than several. Wrapping the title, image and category
 * in separate anchors would give a keyboard user three tab stops to reach the same
 * destination and a screen reader three near-identical links to distinguish between.
 *
 * `nonInteractive` renders the identical card without the link. The admin editor's preview
 * needs that: a preview of a card is not a way to navigate to it, and following the link
 * would take an author out of the editor and away from unsaved work. Swapping the wrapper
 * element is the honest way to do it — intercepting the click would leave something that
 * announces itself as a link and then refuses to behave like one.
 */
export function PostCard({
  post,
  nonInteractive = false,
}: {
  post: PostListItem;
  nonInteractive?: boolean;
}): ReactNode {
  const body = (
    <>
      <div className="aspect-[16/10] w-full overflow-hidden bg-surface-wash">
        {post.coverMediaId ? (
          <img
            src={mediaUrl(post.coverMediaId, 'medium')}
            srcSet={mediaSrcSet(post.coverMediaId)}
            sizes={CARD_IMAGE_SIZES}
            // Alt text is empty on purpose: the adjacent heading already names the
            // article, so describing the decorative cover would be duplicate noise.
            // An empty alt tells assistive technology to skip it, which is correct —
            // omitting the attribute entirely would make it read the filename.
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div
            aria-hidden="true"
            className="size-full bg-gradient-to-br from-primary/10 via-surface-wash to-secondary-container/20"
          />
        )}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-6">
        <div className="flex items-center gap-2">
          {post.category ? <Chip>{post.category.name}</Chip> : null}
          <span className="text-xs text-muted">{post.readingMinutes} min read</span>
        </div>

        <h3 className="text-[1.1rem] font-semibold leading-snug text-heading">{post.title}</h3>

        {post.excerpt ? (
          // Two lines, so a grid of cards keeps a consistent height without the
          // excerpts being truncated mid-word by a character count.
          <p className="line-clamp-2 flex-1 text-[0.95rem] leading-relaxed text-body">
            {post.excerpt}
          </p>
        ) : null}

        <div className="mt-2 flex items-center gap-2 text-xs text-muted">
          {post.author ? <span>{post.author.name}</span> : null}
          {post.author && post.publishedAt ? <span aria-hidden="true">·</span> : null}
          {post.publishedAt ? (
            // A machine-readable <time> element, so the date is available to
            // assistive technology and crawlers in an unambiguous format.
            <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
          ) : null}
        </div>
      </div>
    </>
  );

  return (
    <Card variant="interactive" padding="none" className="group overflow-hidden">
      {nonInteractive ? (
        <div className="flex h-full flex-col">{body}</div>
      ) : (
        <Link to={`/blog/${post.slug}`} className="flex h-full flex-col">
          {body}
        </Link>
      )}
    </Card>
  );
}
